<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Agents</div>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': activeFilter === 'all' }" @click="activeFilter = 'all'">
        <span class="fm-view-dot" style="background:#CBD5E1"></span>All
        <span class="fm-view-count">{{ agents.length }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': activeFilter === 'online' }" @click="activeFilter = 'online'">
        <span class="fm-view-dot" style="background:#10B981"></span>Online
        <span class="fm-view-count">{{ onlineCount }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': activeFilter === 'offline' }" @click="activeFilter = 'offline'">
        <span class="fm-view-dot" style="background:#EF4444"></span>Offline
        <span class="fm-view-count">{{ offlineCount }}</span>
      </button>
    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Agents</div>
            <button class="fm-btn fm-btn--primary" @click="showAddAgentModal"><i class="fa-solid fa-plus"></i>Register Agent</button>
          </div>
          <div class="fm-page-desc">Connect and monitor edge agents that broker secure tunnels to your services.</div>

          <div class="fm-toolbar">
            <div class="fm-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <b-form-input type="search" v-model="searchQuery" placeholder="Search agents by name..."></b-form-input>
            </div>
          </div>

          <div v-if="filteredAgents.length > 0" class="fm-list">
            <div v-for="agent in filteredAgents" :key="agent.name" class="fm-row" @click="openRow(agent)">
              <div class="fm-row-main">
                <div class="fm-row-top">
                  <span class="fm-row-name">{{ agent.name }}</span>
                  <i class="fa-regular fa-copy fm-row-copy"></i>
                </div>
                <div class="fm-row-desc">Host · {{ agent.hostname ? agent.hostname : 'NA' }}</div>
              </div>
              <span v-if="agent.ip" class="fm-value-pill">{{ agent.ip }}</span>
              <div class="fm-status-cell">
                <span class="fm-status-pill" :class="agent.status === 'active' ? 'fm-status-pill--suc' : 'fm-status-pill--err'">
                  <span class="fm-dot"></span>{{ agent.status === 'active' ? 'Online' : 'Offline' }}
                </span>
              </div>
              <i class="fa-solid fa-angle-right fm-chev"></i>
            </div>
          </div>
          <div v-else-if="agents.length > 0" class="fm-empty">
            No agents match this filter.
          </div>
          <div v-else class="fm-empty">
            No agents yet.
            <div class="mt-3"><button class="fm-btn fm-btn--primary" @click="showAddAgentModal"><i class="fa-solid fa-plus"></i>Register Agent</button></div>
          </div>
        </div>

        <!-- Add Agent Modal -->
        <b-modal v-model="showModal" title="Add Agent" hide-footer>
          <b-form @submit.stop.prevent="handleAddAgent">
            <div class="row">
              <div class="col-md-12">
                <b-form-group label="Name" label-for="agent-name">
                  <b-form-input id="agent-name" v-model="newAgent.agent_name" required></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="text-center mt-4">
              <b-button class="btn-fill" type="submit" variant="primary">Add</b-button>
            </div>
          </b-form>
        </b-modal>

        <!-- Activation Instructions Modal -->
        <b-modal v-if="user.role === 'admin'" v-model="showActivationModal" title="Activate Agent" hide-footer
                 size="xl">
          <b-overlay :show="downloading" spinner small>
            <b-card no-body>
              <b-list-group flush>
                <b-list-group-item>
                  <div class="terminal-window">
                    <div class="terminal-header">
                      <div class="terminal-buttons">
                        <span class="terminal-btn close"></span>
                        <span class="terminal-btn minimize"></span>
                        <span class="terminal-btn maximize"></span>
                      </div>
                      <div class="terminal-title">Terminal</div>
                    </div>
                    <div class="terminal-content">
                      <div class="d-flex justify-content-between align-items-start">
                        <pre><code class="language-bash">{{ codeSnippet }}</code></pre>
                        <i class="fa fa-clipboard clickable-icon copy-icon"
                           @click="copyToClipboard(codeSnippet, 'snippet')"
                           :class="{ 'text-success': copyStates.snippet }"
                           :title="copyStates.snippet ? 'Copied!' : 'Copy to clipboard'">
                        </i>
                      </div>
                    </div>
                  </div>
                </b-list-group-item>
                <b-list-group-item>Execute the script on your server and enter the following values when prompted
                </b-list-group-item>
              </b-list-group>
              <b-list-group>
                <b-list-group-item>
                  <i class="fa fa-clipboard clickable-icon copy-icon"
                     @click="copyToClipboard(rowToken, 'token')"
                     :class="{ 'text-success': copyStates.token, 'mr-3': true }"
                     :title="copyStates.token ? 'Copied!' : 'Copy to clipboard'">
                  </i>
                  Agent token: {{ rowToken }}
                </b-list-group-item>
                <b-list-group-item>
                  <i class="fa fa-clipboard clickable-icon copy-icon"
                     @click="copyToClipboard(user.authCode, 'authcode')"
                     :class="{ 'text-success': copyStates.authcode, 'mr-3': true }"
                     :title="copyStates.authcode ? 'ßCopied!' : 'Copy to clipboard'">
                  </i>
                  Auth code: {{ user.authCode }}
                </b-list-group-item>
                <b-list-group-item>Download the agent certificates and move them to the server</b-list-group-item>
                <b-list-group-item>
                  <b-button @click="downloadCertificates(rowToken)" variant="primary" class="btn btn-sm btn-fill">
                    Download Agent Certificates
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-overlay>
        </b-modal>

        <b-modal v-model="showStopModal" title="Stop Agent" hide-footer size="xl">
          <b-card no-body>
            <b-list-group>
              <b-list-group-item>Linux:
                <pre class="mt-1">cloudam-agent stop</pre>
              </b-list-group-item>
              <b-list-group-item>Docker: Stop the docker container</b-list-group-item>
              <b-list-group-item>K8s: Terminate the agent pod</b-list-group-item>
            </b-list-group>
          </b-card>
        </b-modal>

        <!-- Delete Agent Confirmation Modal -->
        <b-modal v-model="showDeleteModal" title="Delete Agent" hide-footer>
          <div class="text-center">
            <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h5>Are you sure you want to delete this agent?</h5>
            <p class="text-muted">
              <strong>Agent:</strong> {{ agentToDelete && agentToDelete.name }}<br>
              This action cannot be undone.
            </p>
            <div class="mt-4">
              <b-button variant="secondary" @click="showDeleteModal = false" class="mr-2">
                Cancel
              </b-button>
              <b-button variant="danger" @click="deleteAgent" :disabled="deleting">
                <span v-if="deleting">
                  <i class="fa fa-spinner fa-spin"></i> Deleting...
                </span>
                <span v-else>
                  <i class="fa fa-trash"></i> Delete Agent
                </span>
              </b-button>
            </div>
          </div>
        </b-modal>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Agent"
      :title="selected.name || ''"
      :status="selected.status === 'active' ? 'Online' : 'Offline'"
      :tone="selected.status === 'active' ? 'suc' : 'err'"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button v-if="user.role === 'admin' && selected.status === 'inactive'" class="fm-btn fm-btn--ghost"
                @click="drawerAction('setup')">
          <i class="fa-solid fa-gear"></i>Setup
        </button>
        <button v-if="user.role === 'admin' && selected.status === 'active'" class="fm-btn fm-btn--danger"
                @click="drawerAction('stop')">
          <i class="fa-solid fa-stop"></i>Stop
        </button>
        <button v-if="user.role === 'admin' && selected.status === 'inactive'" class="fm-btn fm-btn--danger"
                @click="drawerAction('delete')">
          <i class="fa-solid fa-trash"></i>Delete
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import apiClient from '@/http';
import DetailDrawer from '@/components/DetailDrawer.vue';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css'; // Importing the Prism.js theme
import {BModal, BForm, BFormGroup, BFormInput, BButton, BOverlay, BCard, BListGroup, BListGroupItem} from 'bootstrap-vue';

export default {
  components: {
    DetailDrawer,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BCard,
    BListGroup,
    BListGroupItem
  },
  data() {
    return {
      copyStates: {
        snippet: false,
        token: false,
        authcode: false
      },
      searchQuery: '',
      activeFilter: 'all',
      showModal: false,
      showActivationModal: false,
      showStopModal: false,
      showDeleteModal: false,
      activationSteps: [],
      user: {
        authCode: '',
        role: ''
      },
      activationCommand: '',
      newAgent: {
        agent_name: ''
      },
      agents: [],
      totalAgents: 0,
      loading: false,
      downloading: false,
      deleting: false,
      rowToken: '',
      codeSnippet: '',
      agentToDelete: null,
      drawerOpen: false,
      selected: {},
      refreshInterval: null, // For auto-refresh
      lastUpdated: null // Track when data was last updated
    };
  },
  computed: {
    filteredAgents() {
      let list = this.agents;
      if (this.activeFilter === 'online') {
        list = list.filter(a => a.status === 'active');
      } else if (this.activeFilter === 'offline') {
        list = list.filter(a => a.status === 'inactive');
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(a => (a.name || '').toLowerCase().includes(q));
      }
      return list;
    },
    onlineCount() {
      return this.agents.filter(a => a.status === 'active').length;
    },
    offlineCount() {
      return this.agents.filter(a => a.status === 'inactive').length;
    },
    drawerFields() {
      const a = this.selected;
      if (!a || !a.name) return [];
      return [
        {label: 'Status', value: a.status === 'active' ? 'Online' : 'Offline'},
        {label: 'IP Address', value: a.ip ? a.ip : 'NA', mono: true},
        {label: 'Hostname', value: a.hostname ? a.hostname : 'NA', mono: true},
        {label: 'Last Seen', value: a.lastseen},
        {label: 'Token', value: a.token, mono: true},
      ];
    },
  },
  methods: {
    openRow(agent) {
      this.selected = agent;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const agent = this.selected;
      this.closeDrawer();
      if (type === 'setup') {
        this.showActivationInstructions(agent.token, this.user.authCode, 'linux', 'amd64');
      } else if (type === 'stop') {
        this.showStopAgentModal();
      } else if (type === 'delete') {
        this.confirmDeleteAgent(agent);
      }
    },
    async copyToClipboard(text, type) {
      try {
        await navigator.clipboard.writeText(text);
        this.copyStates[type] = true;
        setTimeout(() => {
          this.copyStates[type] = false;
        }, 2000);
        this.showNotification('Copied to clipboard', 'success');
      } catch (err) {
        this.showNotification('Failed to copy', 'danger');
      }
    },

    showAddAgentModal() {
      this.showModal = true;
    },

    showStopAgentModal() {
      this.showStopModal = true;
    },

    confirmDeleteAgent(agent) {
      this.agentToDelete = agent;
      this.showDeleteModal = true;
    },

    async deleteAgent() {
      if (!this.agentToDelete) return;

      this.deleting = true;
      try {
        await apiClient.delete(`/agents/${this.agentToDelete.name}`);
        this.showNotification('Agent deleted successfully', 'success');
        this.showDeleteModal = false;
        this.agentToDelete = null;
        await this.fetchAgents();
      } catch (error) {
        let errorMessage = 'Failed to delete agent';
        if (error.response && error.response.data) {
          errorMessage = error.response.data;
        }
        this.showNotification(errorMessage, 'danger');
      } finally {
        this.deleting = false;
      }
    },

    async fetchUserData() {
      try {
        const response = await apiClient.head('/user');
        this.user.authCode = response.headers['x-user-code'] ? response.headers['x-user-code'] : response.headers['X-User-Code'];
        this.user.role = response.headers['x-user-role'] ? response.headers['x-user-role'] : response.headers['X-User-Role'];
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }

    },


    async showActivationInstructions(token, authCode, os, arch) {
      try {
        this.rowToken = token;
        if (os === 'docker') {
          this.codeSnippet = `docker run -d --network host -v $(pwd)/agent_${token}_certificate.pem:/root/agent_${token}_certificate.pem ghcr.io/winash/cloudam-agent:1.0 -c ${authCode} -t ${token} -i agent_${token}_certificate.pem`
        } else {
          this.codeSnippet = '$ curl -sSL https://api.cloudam.io/downloads/scripts/installer/' + os + '/' + arch + ' | bash -s ';
        }
        this.showActivationModal = true;
      } catch (error) {
        this.showNotification('Error showing activation instructions', 'danger');
      }

    },

    async downloadCertificates(agentToken) {
      try {
        this.downloading = true
        const response = await apiClient.get(`/agents/${agentToken}/certificates`, {responseType: 'blob'});
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `agent_${agentToken}_certificate.pem`); // Adjust file name as needed
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloading = false
      } catch (error) {
        this.downloading = false
        console.error('Failed to download certificates:', error);
        this.showNotification('Error downloading certificates', 'danger');
      }
    },

    async handleAddAgent() {
      try {
        const response = await apiClient.put('/agents', this.newAgent);
        this.agents.push(response.data);
        this.newAgent = {agent_name: ''};
        this.showModal = false;
        await this.fetchAgents();
        this.updateStats();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error adding agent';
        this.showNotification(errorMessage, 'danger');
      }
    },
    async fetchAgents() {
      this.loading = true;
      try {
        const response = await apiClient.get('/agents');
        this.agents = response.data.map(agent => {
          // Determine status based on IP address and connection data
          const isActive = agent.IpAddr !== '' && agent.IpAddr !== null;
          const lastSeenDate = agent.LastSeen ? new Date(agent.LastSeen) : null;

          return {
            name: agent.Name,
            token: agent.AgentToken,
            ip: agent.IpAddr,
            hostname: agent.HostName,
            status: isActive ? 'active' : 'inactive',
            lastseen: isActive && lastSeenDate ?
              this.getRelativeTime(lastSeenDate) :
              (lastSeenDate ? `Last seen: ${lastSeenDate.toLocaleString()}` : 'Never connected'),
            lastSeenRaw: agent.LastSeen,
            showToken: false
          };
        });
        this.updateStats();
        this.lastUpdated = new Date(); // Update timestamp
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching agents';
        this.showNotification(errorMessage, 'danger');
      } finally {
        this.loading = false;
      }
    },
    updateStats() {
      this.totalAgents = this.agents.length;
    },
    toggleShowToken(agent) {
      agent.showToken = !agent.showToken;
    },
    showNotification(message, type) {
      this.$bvToast.toast(message, {
        title: `Info`,
        variant: type,
        solid: true
      })
    },

    getRelativeTime(date) {
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      }
    },
  },
  mounted() {
    this.fetchUserData();
    this.fetchAgents();
    Prism.highlightAll();

    // Set up auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      if (!this.loading) {
        this.fetchAgents();
      }
    }, 30000);
  },

  beforeDestroy() {
    // Clean up interval when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }
};
</script>

<style scoped>
.terminal-window {
  background-color: #2d2d2d;
  border-radius: 10px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
  font-family: "Courier New", Courier, monospace;
  color: #f8f8f2;
}

.terminal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #333;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  padding: 10px;
}

.terminal-buttons {
  display: flex;
  align-items: center;
}

.terminal-btn {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 5px;
}

.terminal-btn.close {
  background-color: #ff5f56;
}

.terminal-btn.minimize {
  background-color: #ffbd2e;
}

.terminal-btn.maximize {
  background-color: #27c93f;
}

.terminal-title {
  font-size: 14px;
  color: #ddd;
}

.terminal-content {
  padding: 20px;
  background-color: #1e1e1e;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  overflow: auto;
  position: relative;
}

.clickable-icon {
  cursor: pointer;
  padding: 5px;
}

.clickable-icon:hover {
  color: #007bff; /* Change color on hover if needed */
}

.copy-icon {
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6c757d;
}

.copy-icon:hover {
  color: #007bff;
}

.copy-icon.text-success {
  color: #28a745;
}

.terminal-content .fa-clipboard {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(30, 30, 30, 0.8);
  border-radius: 4px;
}

pre {
  margin: 0;
}
</style>
